import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, getSupabase, isSupabaseConfigured } from "../lib/supabase";

export interface ShieldWallStatus {
  last_built: string | null;
  new_instruments_since: number;
}

/** Last Shield Wall build + how many new instrument docs have landed since (drives the pulse). */
export function useShieldWallStatus(caseId: string | undefined, poll: boolean) {
  return useQuery<ShieldWallStatus, Error>({
    queryKey: ["shield-wall-status", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    refetchInterval: poll ? 4000 : false,
    staleTime: poll ? 0 : 30 * 1000,
    queryFn: async () => {
      if (!caseId) return { last_built: null, new_instruments_since: 0 };
      const { data, error } = await berserkr().rpc("fetch_shield_wall_status", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as ShieldWallStatus) ?? { last_built: null, new_instruments_since: 0 };
    },
  });
}

/**
 * Build the Shield Wall — re-extract per-grant authorities from every currently-included
 * instrument document (trust / will / POA / corporate / license / permit). The edge function
 * runs in the background (202), so we poll the status until a new run lands, then refresh
 * the wall. Mirrors "Sound the Horn", but asynchronous.
 */
export function useBuildShieldWall(caseId: string | undefined) {
  const qc = useQueryClient();
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef<string | null>(null); // last_built captured at kick-off

  const status = useShieldWallStatus(caseId, building);
  const lastBuilt = status.data?.last_built ?? null;

  // a new run landed → the build finished; refresh the wall and stand down.
  useEffect(() => {
    if (!building) return;
    if (lastBuilt && lastBuilt !== startedAt.current) {
      setBuilding(false);
      qc.invalidateQueries({ queryKey: ["case-authorities", caseId] });
      qc.invalidateQueries({ queryKey: ["shield-wall-status", caseId] });
    }
  }, [building, lastBuilt, caseId, qc]);

  // safety net: never spin forever
  useEffect(() => {
    if (!building) return;
    const t = setTimeout(() => {
      setBuilding(false);
      setError("build is taking longer than expected — check back shortly");
    }, 150 * 1000);
    return () => clearTimeout(t);
  }, [building]);

  const build = useCallback(async () => {
    if (!caseId || building) return;
    const sb = getSupabase();
    if (!sb) { setError("Supabase not configured"); return; }
    setError(null);
    startedAt.current = lastBuilt;
    setBuilding(true);
    const { error: invokeErr } = await sb.functions.invoke("build-shield-wall", { body: { case_id: caseId } });
    if (invokeErr) { setError(invokeErr.message); setBuilding(false); }
  }, [caseId, building, lastBuilt]);

  return { build, building, error, status: status.data };
}
